import type { ShopSessionCustomerFragment } from '@/services/graphql/generated'
import type { PriceIntent } from '@/services/priceIntent/priceIntent.types'
import { getShouldCollectEmail } from '@/utils/customer'
import type { InputField } from './Field.types'
import { MIXED_BREED_OPTION_ID } from './Field.types'
import type { Form, JSONData, PriceTemplate, SectionItem } from './PriceCalculator.types'

const convertTemplateIntoForm = (template: PriceTemplate): Form => {
  return {
    sections: template.sections.map((section) => {
      return { ...section, state: 'initial' }
    }),
  }
}

type FillDataParams = { form: Form; data: JSONData; valueField?: 'value' | 'defaultValue' }

export const prefillData = ({ form, data, valueField = 'defaultValue' }: FillDataParams): Form => {
  return {
    ...form,
    sections: form.sections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        const dataValue = data[item.field.name]

        let parsedValue = dataValue

        if (item.field.type === 'radio') {
          parsedValue = typeof dataValue === 'object' ? JSON.stringify(dataValue) : dataValue?.toString()
        }

        return {
          ...item,
          field: {
            ...item.field,
            // Handle falsey values (e.g. 0) which should be prefilled
            ...(parsedValue != null && { [valueField]: parsedValue }),
          },
        }
      }),
    })),
  }
}

type SetupFormOptions = {
  customer: ShopSessionCustomerFragment | null | undefined
  priceIntent: PriceIntent
  template: PriceTemplate
  prefillAddress?: boolean
}

export const setupForm = ({ customer, priceIntent, template, prefillAddress = true }: SetupFormOptions) => {
  const form = convertTemplateIntoForm(template)

  form.sections.forEach((section) => {
    section.items = section.items.filter((item) => item.field.name !== 'email' || getShouldCollectEmail(customer))
  })

  const existingFormData: JSONData = { ...priceIntent.data }

  if (prefillAddress && priceIntent.suggestedData && priceIntent.data.containsSuggestedData !== true) {
    // Prefill all suggested fields from lookup (e.g. livingSpace, subType).
    // PII address fields (street, zipCode, city) are included here but MaskedAddressField
    // handles their display — it never seeds masked values into text inputs.
    //
    // Once `containsSuggestedData=true`, the server has already merged suggestedData into
    // priceIntent.data with the user's edits taking precedence, so re-overlaying suggestedData
    // here would stomp on those edits (e.g. revert a manually edited livingSpace back to the
    // original lookup value). We only overlay when the server has not yet merged — i.e. on
    // initial form load or when the user opts back in to using their registration address.
    Object.assign(existingFormData, priceIntent.suggestedData)
  }

  const formWithDefaultValues = prefillData({
    form,
    data: existingFormData,
  })

  return updateFormState({
    form: formWithDefaultValues,
    values: { ...existingFormData, ssn: customer?.ssn },
  })
}

type UpdateFormStateParams = {
  form: Form
  values: JSONData
}

export const updateFormState = ({ form, values }: UpdateFormStateParams): Form => {
  return {
    ...form,
    sections: form.sections.map((section) => {
      return {
        ...section,
        state: calculateSectionState(section.items, values),
      }
    }),
  }
}

export const shouldConfirmPriceCalculator = (
  sections: Array<{ id: string }>,
  activeSectionId: string,
  isReadyToConfirm: boolean,
) => {
  const activeSectionIndex = sections.findIndex((section) => section.id === activeSectionId)
  const isLastSection = activeSectionIndex === sections.length - 1
  return isLastSection && isReadyToConfirm
}

const calculateSectionState = (items: Array<SectionItem>, values: JSONData) => {
  const isCompleted = items.every((item) => {
    const isOptional = !item.field.required
    const hasValue = values[item.field.name] != null

    return isOptional || hasValue
  })

  return isCompleted ? 'valid' : 'initial'
}

export const deserializeField = (field: InputField, formData: FormData) => {
  switch (field.type) {
    case 'number':
    case 'stepper':
      return parseNumber(field, formData)

    case 'pet-dog-breeds':
    case 'pet-cat-breeds':
      return parsePetBreedsField(field, formData)

    case 'text':
    case 'radio':
    case 'select':
    case 'date':
    default:
      return parseString(field, formData)
  }
}

const isFieldValueString = (value: FormDataEntryValue | null): value is string => typeof value === 'string'

const parseString = (field: InputField, formData: FormData) => {
  const value = formData.get(field.name)

  if (isFieldValueString(value)) {
    return value
  }

  return field.defaultValue
}

const parseNumber = (field: InputField, formData: FormData) => {
  const value = formData.get(field.name)

  if (isFieldValueString(value)) {
    return parseInt(value, 10)
  }

  return field.defaultValue
}

const parsePetBreedsField = (field: InputField, formData: FormData) => {
  const values = formData.getAll(field.name)

  if (values.length > 0 && values.every(isFieldValueString)) {
    // Removes 'Mixed Breed' option (in case it's present) before form submission
    return values.filter((breedId) => breedId !== MIXED_BREED_OPTION_ID)
  }

  return field.defaultValue
}

export async function getPriceTemplate(templateName: string): Promise<PriceTemplate> {
  try {
    const module_ = await import(`@/features/priceCalculator/priceTemplates/${templateName}`)
    const template = module_.default
    if (typeof template !== 'object' || template.name !== templateName) {
      throw new Error('Template module does not export expected default value')
    }
    return template as PriceTemplate
  } catch (err) {
    throw new Error(`Failed to find priceTemplate ${templateName}`, { cause: err })
  }
}
